import React, { useEffect, useState, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';

export default function Qualifications() {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);
  const controls = useAnimation();
  const textControls = useAnimation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isInView) {
      controls.start({ 
        opacity: 1, 
        y: 0, 
        transition: { duration: 0.8, ease: "easeOut" } 
      });
      textControls.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, staggerChildren: 0.15 }
      });
    } else {
      controls.start({ opacity: 0, y: 50 });
      textControls.start({ opacity: 0, y: 50 });
    }
  }, [isInView, controls, textControls]);

  const qualifications = [
    {
      title: 'M.Com in Finance',
      subtitle: 'University of Calicut (2022–2024)',
      extra: 'Best Student Award, 2024',
      icon: '🎓'
    },
    {
      title: 'Founder & Director – Necttos OPC Pvt. Ltd.',
      subtitle: 'First recognized startup under Calicut University',
      extra: 'Officially launched on July 6, 2024',
      icon: '🚀'
    },
    { 
      title: 'Awarded Best Student Entrepreneur – Merit Day, 2023',
      icon: '🏆'
    },
    {
      title: 'Performance Appreciation Award – NAAC, 2022',
      extra: 'For IT Support, Data Structuring, and IT Management',
      icon: '⭐'
    },
    {
      title: 'Experience in IT Solutions Development & Tech-Driven Educational Platforms',
      extra: '(Virtual College Automation Systems)',
      icon: '💻'
    },
    {
      title: 'Strong Leadership & Organizational Development Skills',
      extra: 'Focused on innovation, impact, and building high-performing teams',
      icon: '👥'
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-24 px-4 sm:px-8 md:px-16 lg:px-24 xl:px-32"
      style={{
		background: 'linear-gradient(180deg, rgb(4, 28, 56) 0%, rgb(3, 18, 35) 76.97%)',
        overflow: 'hidden',
      }}
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-blue-900 rounded-full filter blur-3xl opacity-20 mix-blend-multiply"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-900 rounded-full filter blur-3xl opacity-20 mix-blend-multiply"></div>
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-teal-500 rounded-full filter blur-2xl opacity-10"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div 
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 50 }}
          animate={controls}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-400">
            Qualifications & Credentials
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-purple-500 mx-auto rounded-full"></div>
        </motion.div>

        {/* Qualifications List */}
<div className="space-y-6">
          {qualifications.map((item, index) => (
            <motion.div
              key={index}
              className="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500"
              initial={{ opacity: 0, y: 20 }}
              animate={controls}
              transition={{ delay: index * 0.1 + 0.3 }}
            >
              <h3 className="text-xl font-semibold mb-2 text-blue-400">
                {item.title}
              </h3>
              {item.subtitle && (
                <p className="text-gray-300 mb-2">{item.subtitle}</p>
              )}
              {item.extra && (
                <p className="text-gray-400">{item.extra}</p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}